import { Alert, Card, CardContent, CardHeader } from "@mui/material"

import React, { useEffect, useState } from "react"
import Toolbar, { EditButton } from "../core/Toolbar"

import CustomObjectFieldTabs from "./CustomObjectFieldTabs"
import { DisableMessage } from "../../utils/DisableMessage"
import { Form } from "@rjsf/mui"
import { IChangeEvent } from "@rjsf/core"
import { GlobalRafflesConfig } from "./GlobalRaffleConfig"
import { CountryLocationSelector, LocationProvider } from "../../LocationSelector"
import { auth } from "../../firebase"
import { deleteCollectionAndImages } from "../../GenericList"
import { handleUpload } from "../../utils"
import { useAppConfiguration } from "../AppConfigurationService"
import { useAuthState } from "react-firebase-hooks/auth"
import { useNavigate } from "react-router-dom"
import validator from "@rjsf/validator-ajv8"
import { DateTimeWidget, ImageWidget } from "../CustomWidgets"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { formatImageUploadProgressMessage, resolveImageUploadPrevValue } from "./RaffleEditView.utils"
import { PopupService } from "react-ui-components/components/PopupService"
import { raffleSchema } from "react-ui-components/components/raffle/raffleSchema"
import { raffleUISchema } from "react-ui-components/components/raffle/raffleUISchema"
import { normalizeRaffleMediaItems } from "react-ui-components/components/raffle/raffleMediaUtils"
import Raffles from "react-ui-components/components/Raffles"
import { IRaffle } from "react-ui-components/components/models"
import {
  ALLOWED_ZIP_HELP_TEXT,
  normalizeAllowedZipForSave,
  normalizeRaffleForForm,
  prepareRaffleForSave,
  validateRaffleForm,
} from "./raffleFormUtils"
import { WeeklyScheduleField } from "./WeeklyScheduleField"
import { insertWeeklyScheduleProperty } from "./weeklyScheduleSchema"
import {
  assertRaffleMediaItemsSafeForPersistence,
  stripTemporaryRaffleMediaFields,
} from "./raffleMediaPersistenceGuards"

interface CountryUIWidgetProps {
  onChange: (value: any) => void
  [key: string]: any
}

function CountryUIWidget({ onChange, ...rest }: CountryUIWidgetProps) {
  function onHandleChange(_name: string, value: any) {
    onChange(value)
  }
  return (
    <LocationProvider {...rest}>
      <CountryLocationSelector {...rest} onChange={onHandleChange} />
    </LocationProvider>
  )
}

type EditableRaffle = IRaffle & {
  createdBy?: string
  raffleMediaItems?: any[]
  publishNotification?: { imageUrl?: string; [key: string]: any }
}

interface RaffleEditViewProps {
  raffle: EditableRaffle | null | undefined
  rafflesConfig?: any
  onRaffleSaved?: (raffle: EditableRaffle) => void
}

const RaffleForm = ({
  raffle,
  raffleSchema,
  raffleUISchema,
  handleFormChange,
  onSubmit,
}: {
  raffle: IRaffle
  raffleSchema: any
  raffleUISchema: any
  handleFormChange: (e: IChangeEvent) => void
  onSubmit: (e: IChangeEvent) => void
}) => (
  <Card>
    <CardContent>
      <div className="h-screen overflow-auto">
        <Form
          formData={raffle}
          widgets={{ DateTimeWidget }}
          schema={{
            ...raffleSchema,
            properties: {
              ...insertWeeklyScheduleProperty(raffleSchema.properties),
              rafflesImagesLink: { type: "string", title: "Raffles Images Link" },
              // Added locally (same pattern as rafflesImagesLink above) because this
              // repo's react-ui-components dependency is pinned to a specific commit
              // and won't pick up raffleSchema's own "allowedZip" field until the
              // library is released and the pin is updated. Format is enforced by
              // customValidate below rather than a schema `pattern`, so an empty
              // value (no restriction) doesn't fail validation.
              allowedZip: {
                type: "string",
                title: "Allowed ZIP Code (5 digits, optional - leave empty for no restriction)",
              },
            },
          }}
          uiSchema={{
            ...raffleUISchema,
            ticketDesign: {
              ...raffleUISchema.ticketDesign,
              ticketImage: {
                "ui:widget": ImageWidget,
              },
            },
            country: {
              "ui:widget": CountryUIWidget,
            },
            raffleLocation: {
              country: {
                "ui:widget": CountryUIWidget,
              },
            },
            // Recurring-raffle section under "Raffle Date" / "Visible From".
            weeklySchedule: {
              "ui:field": WeeklyScheduleField,
              "ui:options": { tab: "general" },
            },
            allowedZip: {
              ...(raffleUISchema as any)?.allowedZip,
              "ui:emptyValue": "",
              "ui:help": ALLOWED_ZIP_HELP_TEXT,
            },
          }}
          validator={validator}
          customValidate={validateRaffleForm}
          onChange={handleFormChange}
          onSubmit={((formValue: any) => {
            if (!onSubmit) { return; }

            const data = formValue.formData
            formValue.formData = {
              ...data,
              allowedZip: normalizeAllowedZipForSave(data?.allowedZip),
              location: {

                country: '',
                city: '',
                province: '',
                ...data.location
              },

            }

            onSubmit(formValue)
          }) as any}
          templates={{
            ObjectFieldTemplate: CustomObjectFieldTabs,
          }}
          formContext={{ className: "m-5", raffleDisabled: Boolean(raffle?.disabled) }}
        />
      </div>
    </CardContent>
  </Card>
)

const RaffleDisplay = ({
  raffle,
  user,
  config,
}: {
  raffle: IRaffle
  user: any
  config: any
}) => (
  <Card>
    <CardContent>
      <div className="h-screen overflow-auto">
        <DisableMessage disabled={raffle.disabled} />
        <Raffles
          raffles={[{ ...raffle, disabled: false }]}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          fetchLikes={() => { }}
          user={user}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleLike={async (raffleId: string) => { }}
          rafflesConfig={config}
          termsIconProps={{
            src: require("../../images/termsIcon.png"),
            width: 30,
            height: 30,
            alt: "terms",
          }}
        />
      </div>
    </CardContent>
  </Card>
)

export function RaffleEditView({
  raffle: raffleData,
  rafflesConfig,
  onRaffleSaved,
}: RaffleEditViewProps) {
  raffleData = (raffleData || {}) as EditableRaffle
  const [isEditing, setIsEditing] = useState(false)
  const handleEditClick = () => setIsEditing(!isEditing)
  const navigate = useNavigate()
  const [user] = useAuthState(auth)

  const [raffle, setRaffle] = useState(
    normalizeRaffleForForm(raffleData) || raffleData,
  )
  const { configuration } = useAppConfiguration()
  const [config, setConfig] = useState(configuration.rafflesScreen)
  const { saveRaffle, disableRaffle, duplicateRaffle } = useAppConfiguration()

  useEffect(() => {
    setConfig(rafflesConfig || configuration.rafflesScreen)
  }, [rafflesConfig])

  useEffect(() => {
    setRaffle(normalizeRaffleForForm(raffleData) || raffleData)
  }, [raffleData])

  const handleFormChange = ({ formData }: IChangeEvent) => {
    setRaffle(normalizeRaffleForForm(formData) || formData)
  }

  const onSubmit = async ({ formData }: IChangeEvent) => {
    try {
      LoadingService.show("Saving raffle...")
      const existingCreatedBy =
        typeof raffleData?.createdBy === "string" ? raffleData.createdBy.trim() : raffleData?.createdBy
      const incomingCreatedBy =
        typeof formData?.createdBy === "string" ? formData.createdBy.trim() : formData?.createdBy
      const resolvedCreatedBy = existingCreatedBy || incomingCreatedBy || auth.currentUser?.uid
      const nextFormData = prepareRaffleForSave(
        resolvedCreatedBy ? { ...formData, createdBy: resolvedCreatedBy } : formData,
      ) || formData

      if (nextFormData.raffleImages?.length > 0) {
        // Previously a bare LoadingService.show() with no message covered every upload
        // stage below, so a raffle with several images looked "stuck loading" for as long
        // as they took to upload (Trello #180). This surfaces a running count instead.
        const totalImages = nextFormData.raffleImages.length
        let uploadedImages = 0
        LoadingService.show(formatImageUploadProgressMessage(uploadedImages, totalImages))
        const imagesURLs = await Promise.all(
          nextFormData.raffleImages.map(async (image: string, index: number) => {
            const url = await handleUpload({
              image,
              id: raffle.id,
              fieldName: "assets",
              imagesBucket: "raffles",
              prevValue: raffleData.raffleImages?.[index],
            })
            uploadedImages += 1
            LoadingService.show(formatImageUploadProgressMessage(uploadedImages, totalImages))
            return url
          }),
        )
        nextFormData.raffleImages = imagesURLs
      }

      if (nextFormData.ticketDesign?.ticketImage) {
        LoadingService.show("Uploading ticket design image...")
        nextFormData.ticketDesign = {
          ...nextFormData.ticketDesign,
          ticketImage: await handleUpload({
            image: nextFormData.ticketDesign.ticketImage,
            id: raffle.id,
            fieldName: "ticketDesign",
            imagesBucket: "raffles",
            prevValue: raffleData.ticketDesign?.ticketImage,
          }),
        }
      }

      const normalizedMediaItems = normalizeRaffleMediaItems(nextFormData.raffleMediaItems)
      if (normalizedMediaItems.length > 0) {
        LoadingService.show(`Uploading ${normalizedMediaItems.length} media item(s)...`)
        nextFormData.raffleMediaItems = await Promise.all(
          normalizedMediaItems.map(async (item: any, index: number) => {
            const prevItem = raffleData?.raffleMediaItems?.[index] || {}
            const nextItem = { ...item }
            const uploadSource =
              typeof item.localDataUrl === "string" && item.localDataUrl.trim()
                ? item.localDataUrl
                : item.url

            if (typeof uploadSource === "string") {
              nextItem.url = await handleUpload({
                image: uploadSource,
                id: raffle.id,
                fieldName: `raffleMediaItems-${index}`,
                imagesBucket: "raffles",
                prevValue: prevItem.url,
              })
            }

            if (typeof item.thumbnailUrl === "string") {
              nextItem.thumbnailUrl = await handleUpload({
                image: item.thumbnailUrl,
                id: raffle.id,
                fieldName: `raffleMediaItems-thumbnail-${index}`,
                imagesBucket: "raffles",
                prevValue: prevItem.thumbnailUrl,
              })
            }

            if (Array.isArray(item.additionalUrls)) {
              nextItem.additionalUrls = await Promise.all(
                item.additionalUrls.map(async (entry: any, galleryIndex: number) => {
                  const prevEntry = prevItem?.additionalUrls?.[galleryIndex]

                  if (!entry || typeof entry.url !== "string") {
                    return entry
                  }

                  return {
                    ...entry,
                    url: await handleUpload({
                      image: entry.url,
                      id: raffle.id,
                      fieldName: `raffleMediaItems-gallery-${index}-${galleryIndex}`,
                      imagesBucket: "raffles",
                      prevValue: prevEntry?.url,
                    }),
                  }
                }),
              )
            }

            delete nextItem.localDataUrl
            return nextItem
          }),
        )
      }

      nextFormData.raffleMediaItems = stripTemporaryRaffleMediaFields(nextFormData.raffleMediaItems)
      assertRaffleMediaItemsSafeForPersistence(nextFormData.raffleMediaItems)

      if (nextFormData.goalSettings?.actionIconAsset) {
        LoadingService.show("Uploading goal icon...")
        nextFormData.goalSettings = {
          ...nextFormData.goalSettings,
          actionIconAsset: await handleUpload({
            image: nextFormData.goalSettings.actionIconAsset,
            id: raffle.id,
            fieldName: "goalSettings",
            imagesBucket: "raffles",
            prevValue: resolveImageUploadPrevValue(
              nextFormData.goalSettings.actionIconAsset,
              raffleData.goalSettings?.actionIconAsset,
            ),
          }),
        }
      }

      if (nextFormData.publishNotification?.imageUrl) {
        LoadingService.show("Uploading notification image...")
        nextFormData.publishNotification = {
          ...nextFormData.publishNotification,
          imageUrl: await handleUpload({
            image: nextFormData.publishNotification.imageUrl,
            id: raffle.id,
            fieldName: "publishNotification",
            imagesBucket: "raffles",
            prevValue: resolveImageUploadPrevValue(
              nextFormData.publishNotification.imageUrl,
              raffleData.publishNotification?.imageUrl,
            ),
          }),
        }
      }

      LoadingService.show("Saving raffle...")
      const data = await saveRaffle(nextFormData)
      const normalizedSavedRaffle = normalizeRaffleForForm(data) || data

      setRaffle(normalizedSavedRaffle)
      onRaffleSaved?.(normalizedSavedRaffle)
    } catch (error: any) {
      console.log("Error uploading raffles images: ", formData.raffleImages)
      PopupService.pushPopup(error.message)
    } finally {
      LoadingService.hide()
    }
  }

  const onDuplicate = async () => {
    try {
      LoadingService.show()

      const result = await duplicateRaffle(raffleData)
      navigate(`/raffles/${result.id}`)
    } catch (error: any) {
      console.log("Error duplicating raffle: ", raffleData.raffleImages)
      PopupService.pushPopup(error.message)
    } finally {
      LoadingService.hide()
    }
  }

  return (
    <div>
      <div className="flex flex-row justify-between gap-5 m-2">
        <h1>
          <strong>Raffle ID: </strong>
          {raffle.id}
        </h1>
        <h1>
          <strong>Raffle Name: </strong>
          {raffle.name}
        </h1>
      </div>
      <Card>
        <CardHeader
          avatar={
            <Toolbar
              disabled={raffle.disabled}
              onDelete={async function () {
                PopupService.setConfirm(
                  "Are you sure you want to delete this raffle?",
                  (answer) => {
                    if (!/ok/i.test(answer || "")) {
                      return
                    }

                    ;(async () => {
                      try {
                        LoadingService.setLoading(true)

                        await deleteCollectionAndImages({
                          bucketPath: "rafflesImages",
                          collectionPath: "raffles",
                          documentIds: [raffleData.id],
                        })
                        navigate("/raffles")
                        console.log("Collection and images deleted successfully.")
                      } catch (error: any) {
                        const msg = "Error deleting collection and images"
                        LoadingService.setLoading(true, msg, true)
                        console.error(msg, error)
                        PopupService.pushPopup(`${msg}\n${error.message}`)
                      } finally {
                        LoadingService.setLoading(false)
                      }
                    })()
                  },
                )
              }}
              onDuplicate={onDuplicate}
              onDisable={async function () {
                try {
                  LoadingService.show()
                  const disabled = !raffle.disabled
                  await disableRaffle(raffle.id, disabled)
                  setRaffle({ ...raffle, disabled })
                } catch (error: any) {
                  PopupService.pushPopup(error.message)
                } finally {
                  LoadingService.hide()
                }
              }}
            />
          }
          action={<EditButton onEdit={handleEditClick} />}
        />

        <CardContent className="overflow-auto">
          <GlobalRafflesConfig
            onChange={(formData: any) => {
              setConfig(formData)
            }}
          />

          {isEditing ? (
            <div className="flex flex-row gap-2 overflow-auto" style={{ minHeight: 500 }}>
              <div className="flex-1 overflow-auto min-w-0">
                <RaffleForm
                  raffle={raffle}
                  raffleSchema={raffleSchema}
                  raffleUISchema={raffleUISchema}
                  handleFormChange={handleFormChange}
                  onSubmit={onSubmit}
                />
              </div>
              <div className="flex-1 overflow-auto min-w-0">
                <RaffleDisplay raffle={raffle} user={user} config={config} />
              </div>
            </div>
          ) : (
            <RaffleDisplay raffle={raffle} user={user} config={config} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
